import { useEffect } from 'react';

import { useSetRecoilState } from 'recoil';

import { AdminRouter } from '@route/AdminRouter';
import { BugoRouter } from '@route/BugoRouters/BugoRouter';
import { GiftBrandRouter } from '@route/GiftBrandRouters/GiftBrandRouter';
import { GiftSellerRouter } from '@route/GiftSellerRouters/GiftSellerRouter';
import { GiftStoreRouter } from '@route/GiftStoreRouters/GiftStoreRouter';
import { SellerRouter } from '@route/SellerRouters/SellerRouter';
import { StoreRouter } from '@route/StoreRouters/StoreRouter';
import { ScrollToTop } from '@shared/components/ScrollToTop';
import { useCssVariables } from '@shared/hooks/useCssVariables';
import { KakaoCallbackPage } from '@shared/page/KakaoCallbackPage';
import { notificateError } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { FORCED_LOGOUT_NOTICE_KEY, setAxiosNotifier } from '@shared/plugIn/axios';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { UserCustomTheme } from '@shared/types';
import { mainColorGenerator } from '@shared/utils/mainColorGenerator';
import { notification } from 'antd';
import { Route, Routes, useLocation } from 'react-router-dom';

//* setenv 스크립트(package.json)가 빌드 시 주입
const BUILD_VERSION = `${process.env.REACT_APP_BUILD_DATE ?? 'dev'} (${
  process.env.REACT_APP_COMMIT_ID ?? 'local'
})`;
console.info(`version ${BUILD_VERSION}`);

function App() {
  const { pathname } = useLocation();
  const [api, contextHolder] = notification.useNotification();
  const setNotificationInstance = useSetRecoilState(notificationInstanceAtom);

  //* admin 중에서도 모바일 폭(max-w-screen-md)을 유지할 페이지들
  const adminNarrowPage =
    //* 관리자 홈(런처): 모바일 우선 그리드라 좁게 유지
    pathname === '/admin' ||
    pathname === '/admin/' ||
    pathname.includes('courtesy-manage') ||
    //* 장례식장정보 수정: 넓은 표가 없는 입력 폼이라 와이드 모니터에서 늘어나면 보기 나쁨
    (pathname.includes('funeral-home-info') && pathname.endsWith('edit'));

  //* 표는 넓게 써야 하지만 화면 끝까지 꽉 채우면 눈이 피로한 페이지 —
  //* 가운데 정렬된 일반 페이지 폭으로 둔다 (표는 자체 가로 스크롤을 가진다)
  const adminRoomyPage = pathname.startsWith('/admin/analytics');

  const fullWidthPage =
    pathname.endsWith('-stat') ||
    pathname.endsWith('gift-event-log') ||
    pathname.endsWith('gift-delivery-management') ||
    pathname.endsWith('-stat-new') ||
    pathname.endsWith('price-table') ||
    pathname.includes('/gift-settlement') ||
    pathname.includes('flower-seller') ||
    pathname.includes('gift-seller') ||
    pathname.includes('gift-brand') ||
    //* admin은 넓은 표가 많아 폭 제한을 풀지만, 모바일용 페이지는 제외
    (pathname.includes('admin') && !adminNarrowPage) ||
    pathname.endsWith('account') ||
    pathname.endsWith('gift-dashboard');

  const widthClass = adminRoomyPage
    ? 'max-w-screen-xl'
    : fullWidthPage
    ? ''
    : 'max-w-screen-md';

  useCssVariables([
    {
      property: '--main-color',
      value: mainColorGenerator(UserCustomTheme.Preed),
    },
  ]);

  useEffect(() => {
    setNotificationInstance(api);
    //* axios 인터셉터(React 밖)의 권한 부족 401 안내용
    setAxiosNotifier(api);
  }, [api, setNotificationInstance]);

  //* 무효 토큰(다른 서버용 등)으로 강제 로그아웃된 직후라면 안내 메시지 노출
  useEffect(() => {
    if (sessionStorage.getItem(FORCED_LOGOUT_NOTICE_KEY)) {
      sessionStorage.removeItem(FORCED_LOGOUT_NOTICE_KEY);
      notificateError(api, '유효한 로그인이 아닙니다', '다시 로그인해주세요.', 'top', 3);
    }
  }, [api]);

  return (
    <>
      {contextHolder}
      <div>
        <div className="sr-only">
          <p>version {BUILD_VERSION}</p>
        </div>
        <ScrollToTop />
        <div className={`${UserCustomTheme.Preed} `}>
          <div className="">
            <div
              className={`mx-auto min-h-screen w-full ${widthClass} theme-text-1 theme-bg-6`}
            >
              <Routes>
                <Route path="*" element={<BugoRouter />} />
                <Route path="flower-store/*" element={<StoreRouter />} />
                <Route path="gift-store/*" element={<GiftStoreRouter />} />
                <Route path="flower-seller/*" element={<SellerRouter />} />
                <Route path="gift-seller/*" element={<GiftSellerRouter />} />
                <Route path="gift-brand/*" element={<GiftBrandRouter />} />
                <Route path="admin/*" element={<AdminRouter />} />
                <Route path="social/kakao/auth" element={<KakaoCallbackPage />} />
              </Routes>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default App;
